import { Box, Grid, Paper, Skeleton, Typography } from "@mui/material";

import CopyButton from "../../../components/CopyButton";
import MarkdownRenderer from "../../../components/MarkdownRenderer";
import { ViewerArtifacts } from "../../../data/queries/viewer";
import { formatDurationMmSs } from "../../../utils/date";

const getCallDurationMs = (data: ViewerArtifacts): number | null => {
  const sources = [data.callTranscriptionRecord, data.callDataRecord, data.callRecordingEvent];
  const value = sources
    .map(artifact => artifact?.content?.callEvent?.duration)
    .find(duration => typeof duration === "number" && Number.isFinite(duration) && duration > 0);

  return typeof value === "number" ? value : null;
};

const resolveDurationText = (data: ViewerArtifacts): string | null => {
  const durationMs = getCallDurationMs(data);

  if (durationMs !== null) {
    return formatDurationMmSs(durationMs);
  }

  return data.summary?.content?.durationText ?? null;
};

interface SummaryTabProps {
  data: ViewerArtifacts | null;
  isLoading: boolean;
}

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="subtitle1"
    fontWeight="bold"
    color="primary"
    sx={{ mb: 1, mt: 2 }}
  >
    {children}
  </Typography>
);

const InfoItem = ({ label, value }: { label: string; value: string | null | undefined }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1">
      {value || "Not available"}
    </Typography>
  </Box>
);

export const SummaryTab = ({ data, isLoading }: SummaryTabProps) => {
  if (isLoading) {
    return (
      <Box sx={{ width: "100%" }}>
        <Skeleton variant="text" sx={{ fontSize: "2rem" }} />
        <Skeleton variant="rectangular" height={120} />
        <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
        <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (!data || !data.summary) {
    return (
      <Box>
        <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Summary
          </Typography>
          <Typography variant="body1" color="text.secondary">
            No summary data available for this interaction.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const summary = data.summary.content;

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 0, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <SectionTitle>Intent</SectionTitle>
            <Typography variant="body1" paragraph>
              {summary.intent}
            </Typography>

            <SectionTitle>Summary</SectionTitle>
            <Typography variant="body1" paragraph>
              {summary.summary}
            </Typography>

            {summary.next_step && (
              <>
                <SectionTitle>Suggested Next Steps</SectionTitle>
                <Typography variant="body1">
                  <strong>{summary.next_step.timeframe}:</strong> {summary.next_step.description}
                </Typography>
              </>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper elevation={1} sx={{ p: 2, bgcolor: "background.default" }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Call Details
              </Typography>

              <InfoItem label="Duration" value={resolveDurationText(data)} />
              <InfoItem label="Language" value={summary.language} />
              <InfoItem label="Disposition" value={summary.disposition} />
            </Paper>
          </Grid>
        </Grid>

        {summary.participants && summary.participants.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <SectionTitle>Participants</SectionTitle>
            <Grid container spacing={2}>
              {summary.participants.map((participant: any, index: number) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper elevation={1} sx={{ p: 2, bgcolor: "background.default" }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {participant.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {participant.role} {participant.company ? `at ${participant.company}` : ""}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {summary.extendedSummary && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <SectionTitle>Extended Summary</SectionTitle>
              <CopyButton
                text={summary.extendedSummary}
                tooltipTitle="Copy extended summary"
                richText={true}
              />
            </Box>
            <Paper elevation={1} sx={{ p: 2, bgcolor: "background.default" }}>
              <MarkdownRenderer content={summary.extendedSummary} />
            </Paper>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default SummaryTab;
